/**
 * Preview blocks shared by the "ABC" dialogs: the picture frame with its zoom
 * buttons, the text zone rendered on the customization picture, and the
 * arrow-key nudging of a picture while the mouse is over its row.
 */
import {
  useState,
  type CSSProperties,
  type MouseEvent,
  type ReactNode,
} from 'react';
import { INITIAL_ZOOM, ZOOM_STEP } from './preview-logic';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faSearchMinus, faSearchPlus } from '@fortawesome/free-solid-svg-icons';
import { CircularText } from '@/shared/ui/CircularText';
import { PictureField, type PictureFieldProps } from '@/shared/ui/PictureField';

export interface TextZoneProps {
  style: CSSProperties;
  text: string;
  textureImg: string;
  curve: number | null | undefined;
}

export function TextZone({
  style,
  text,
  textureImg,
  curve,
}: Readonly<TextZoneProps>) {
  return (
    <div className="dp-custom-image-text-zone" style={style}>
      {curve ? (
        <CircularText text={text} textureImg={textureImg} radius={curve} />
      ) : (
        text
      )}
    </div>
  );
}

export interface PreviewFrameProps {
  /** `main` uses the 533x500 card frame with its blue alignment line, `custom` the 462px square. */
  kind: 'main' | 'custom';
  zoom: number;
  src: string;
  imageStyle: CSSProperties;
  children?: ReactNode;
  testId?: string | undefined;
}

export function PreviewFrame({
  kind,
  zoom,
  src,
  imageStyle,
  children,
  testId,
}: Readonly<PreviewFrameProps>) {
  return (
    <div
      className={
        kind === 'main'
          ? 'dp-main-image-container'
          : 'dp-custom-image-container'
      }
      style={{ transform: `scale(${zoom})` }}
    >
      <img src={src} alt="" style={imageStyle} data-testid={testId} />
      {children}
    </div>
  );
}

export type PreviewPictureFieldProps = Omit<
  PictureFieldProps,
  'renderPreview' | 'children' | 'labelClassName'
> & {
  kind: 'main' | 'custom';
  imageStyle: CSSProperties;
  /** Content over the picture (text zone, master watermark). */
  overlay?: ReactNode;
};

/** Picture field with the scaled preview frame and its zoom buttons. */
export function PreviewPictureField({
  kind,
  imageStyle,
  overlay,
  ...field
}: PreviewPictureFieldProps) {
  const [zoom, setZoom] = useState(INITIAL_ZOOM);
  const zoomBy = (event: MouseEvent, step: number) => {
    event.preventDefault();
    event.stopPropagation();
    setZoom((current) => current + step);
  };
  return (
    <PictureField
      {...field}
      labelClassName="preview-frame"
      renderPreview={(src, testId) => (
        <PreviewFrame
          kind={kind}
          zoom={zoom}
          src={src}
          imageStyle={imageStyle}
          testId={testId}
        >
          {overlay}
        </PreviewFrame>
      )}
    >
      <button
        type="button"
        className="zoom-in-icon"
        aria-label={`Zoom in ${field.label.toLowerCase()}`}
        onClick={(event) => zoomBy(event, ZOOM_STEP)}
      >
        <FontAwesomeIcon icon={faSearchPlus} />
      </button>
      <button
        type="button"
        className="zoom-out-icon"
        aria-label={`Zoom out ${field.label.toLowerCase()}`}
        onClick={(event) => zoomBy(event, -ZOOM_STEP)}
      >
        <FontAwesomeIcon icon={faSearchMinus} />
      </button>
    </PictureField>
  );
}
